import { useEffect, useState } from "react";
import Head from "next/head";
import {
  FaYoutube,
  FaPhone,
  FaWhatsapp,
  FaCog,
  FaBriefcase,
  FaGlobe,
  FaCalendarAlt,
  FaCar,
} from "react-icons/fa";
import { SOCIAL_MEDIA_LINKS } from "@/app/utils/socialMedia";
import { BRAND_NAMES, LOJAS_DATA } from "@/app/utils/utils";

const LOJAS_MAPS_LINKS: Record<string, string> = {
  SIA: "https://maps.app.goo.gl/Fy6qSCKsZqyERpZH7",
  TAGUATINGA: "https://maps.app.goo.gl/rSPXAXqM4qgn5WHJ7",
  "EPIA SUL": "https://maps.app.goo.gl/GVDq1R8tn72c1ARU9",
  AEROPORTO: "https://maps.app.goo.gl/7XyyyYg5hZKP6FYx7",
};

const links = [
  {
    name: "Visite o nosso site",
    href: `https://${BRAND_NAMES.URL}.com.br/?utm_source=instagram&utm_medium=linktree&utm_campaign=bio`,
    src: "https://thankful-beach-071b5c410.3.azurestaticapps.net/img/kuruma/consecionaria/site.svg",
    icon: FaGlobe,
    iconColor: "#E50914",
    hoverSrc:
      "https://thankful-beach-071b5c410.3.azurestaticapps.net/img/kuruma/consecionaria/site-hover.svg",
  },
  {
    name: "Atendimento Online",
    href: `https://api.whatsapp.com/send/?phone=556198735298&text=Olá,%20vim%20do%20Instagram%20e%20tenho%20interesse%20em%20saber%20mais%20sobre%20as%20ofertas%20do%20mês%20de%20veículos%20novos`,
    icon: FaWhatsapp,
    iconColor: "#E50914",
  },
  {
    name: "Agendamento Fácil - Oficina",
    href: `https://api.whatsapp.com/send/?phone=${BRAND_NAMES.POS_NUMBER}`,
    src: "https://thankful-beach-071b5c410.3.azurestaticapps.net/img/kuruma/consecionaria/calendario.svg",
    icon: FaCalendarAlt,
    iconColor: "#E50914",
    hoverSrc:
      "https://thankful-beach-071b5c410.3.azurestaticapps.net/img/kuruma/consecionaria/calendario-hover.svg",
  },
  {
    name: "Veículos Seminovos",
    href: `${BRAND_NAMES.SEMINOVOS_SITE_URL}/?utm_source=instagram&utm_medium=linktree&utm_campaign=bio_vn`,
    src: "https://thankful-beach-071b5c410.3.azurestaticapps.net/img/kuruma/consecionaria/carro_2.svg",
    icon: FaCar,
    iconColor: "#E50914",
    hoverSrc:
      "https://thankful-beach-071b5c410.3.azurestaticapps.net/img/kuruma/consecionaria/carro_2_hover.svg",
  },
  {
    name: "YouTube",
    href: `${SOCIAL_MEDIA_LINKS.YOUTUBE}?utm_source=instagram&utm_medium=linktree&utm_content=youtube`,
    icon: FaYoutube,
    iconColor: "#FF0000",
  },
  {
    name: "Peças",
    href: "https://www.pecasredetoyota.com.br/home",
    icon: FaCog,
    iconColor: "#E50914",
  },
  {
    name: "Trabalhe Conosco",
    href: "https://grupoaguiabranca.empregare.com/pt-br",
    icon: FaBriefcase,
    iconColor: "#E50914",
  },
];

const concessionarias = LOJAS_DATA.filter(
  (loja) => LOJAS_MAPS_LINKS[loja.nome]
).map((loja) => ({
  name: `${BRAND_NAMES.DEFAULT} - ${loja.nome}`,
  link: LOJAS_MAPS_LINKS[loja.nome],
  telefone: (loja.telefone ?? "").replace(/[^0-9]/g, ""),
}));

export default function Linktree() {
  const [showLocations, setShowLocations] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Simula um tempo de carregamento mínimo para mostrar o loading
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 1500);

    return () => clearTimeout(timer);
  }, []);

  if (isLoading) {
    return (
      <div className="fixed inset-0 bg-white flex items-center justify-center z-50">
        <div className="flex flex-col items-center">
          <img
            src={BRAND_NAMES.siteLogoMenu}
            alt={`Logo ${BRAND_NAMES.DEFAULT}`}
            className="w-32 h-auto animate-pulse"
          />
          <div className="mt-4 w-32 h-1 bg-gray-200 rounded overflow-hidden">
            <div className="h-full bg-[#2D2D2D] animate-loading"></div>
          </div>
        </div>
        <style jsx>{`
          @keyframes loading {
            0% {
              width: 0%;
              margin-left: 0;
            }
            50% {
              width: 100%;
            }
            100% {
              width: 0%;
              margin-left: 100%;
            }
          }
          .animate-loading {
            animation: loading 2s ease-in-out infinite;
          }
        `}</style>
      </div>
    );
  }

  return (
    <>
      <Head>
        <title>{BRAND_NAMES.META_TITLE} - Links</title>
        <meta
          name="description"
          content={`Links importantes da ${BRAND_NAMES.DEFAULT} Toyota`}
        />
      </Head>

      <div className="min-h-screen bg-gray-100 flex flex-col justify-center items-center p-4 relative">
        {/* Background Image */}
        <div className="absolute inset-0 z-0">
          <img
            src="https://bloblandingpageprd.blob.core.windows.net/toyota/geral/linktree/background_desktop.png"
            alt="Background"
            className="hidden md:block w-full h-full object-cover"
          />
          <img
            src="https://bloblandingpageprd.blob.core.windows.net/toyota/geral/linktree/background_mobile.png"
            alt="Background"
            className="block md:hidden w-full h-full object-cover"
          />
        </div>

        <div className="max-w-2xl w-full mx-auto flex flex-col items-center relative z-10">
          {/* Logo Kyoto */}
          <div className="mb-8">
            <img
              src={BRAND_NAMES.siteLogoMenu}
              alt={`Logo ${BRAND_NAMES.DEFAULT}`}
              className="w-48 h-auto"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-1 gap-4 w-full mb-8">
            <div className="w-full max-w-2xl">
              <button
                onClick={() => setShowLocations(!showLocations)}
                className="w-full bg-[#2D2D2D] text-white px-6 py-3 rounded-lg font-semibold shadow-[0_4px_6px_-1px_rgba(0,0,0,0.1),0_2px_4px_-1px_rgba(0,0,0,0.06)] hover:bg-[#1a1a1a] transform transition-all duration-200 hover:scale-105 flex items-center justify-center gap-2"
              >
                <span className="text-lg">Nossas Concessionárias</span>
                <svg
                  className={`w-5 h-5 transition-transform duration-200 ${showLocations ? "rotate-180" : ""}`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
              </button>

              {/* Modal */}
              {showLocations && (
                <div className="fixed inset-0 z-50 overflow-y-auto" data-nosnippet="true">
                  <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
                    {/* Background overlay */}
                    <div
                      className="fixed inset-0 transition-opacity bg-black bg-opacity-50"
                      onClick={() => setShowLocations(false)}
                    ></div>

                    {/* Modal panel */}
                    <div className="inline-block w-full max-w-2xl p-6 my-8 overflow-hidden text-left align-middle transition-all transform bg-white shadow-xl rounded-2xl" data-nosnippet="true">
                      <div className="flex justify-between items-center mb-4">
                        <h2 className="text-xl font-bold text-gray-800">
                          Encontre a unidade mais próxima
                        </h2>
                        <button
                          onClick={() => setShowLocations(false)}
                          className="text-gray-400 hover:text-gray-500"
                        >
                          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-h-[60vh] overflow-y-auto" data-nosnippet="true">
                        {concessionarias.map((concessionaria, index) => (
                          <div key={index} className="flex flex-col space-y-2" data-nosnippet="true">
                            <a
                              href={concessionaria.link}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="flex items-center p-3 rounded-lg hover:bg-gray-50 transition-colors duration-200 group"
                              data-nosnippet="true"
                            >
                              <div className="mr-3 text-red-500 group-hover:text-red-600">
                                <svg
                                  className="w-5 h-5"
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                                  />
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                                  />
                                </svg>
                              </div>
                              <span className="text-gray-700 group-hover:text-gray-900 font-medium flex-grow">
                                {concessionaria.name}
                              </span>
                            </a>
                            <a
                              href={`tel:+55${concessionaria.telefone}`}
                              className="flex items-center p-3 text-gray-600 hover:text-red-600 transition-colors duration-200"
                            >
                              <FaPhone size={16} className="mr-2" />
                              <span>
                                Telefone fixo:{" "}
                                {concessionaria.telefone.replace(
                                  /(\d{2})(\d{4})(\d{4})/,
                                  "($1) $2-$3"
                                )}
                              </span>
                            </a>
                            {index < concessionarias.length - 1 && (
                              <div className="border-t border-gray-200 my-2"></div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
            {links.map((link, index) => (
              <a
                key={index}
                href={link.href}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center bg-white shadow-md rounded-lg p-4 hover:bg-gray-50 transition transform hover:scale-105 duration-200"
              >
                {link.icon ? (
                  <div className="w-12 h-12 mr-4 flex items-center justify-center">
                    {<link.icon size={32} color={link.iconColor} />}
                  </div>
                ) : (
                  <img
                    src={link.src}
                    alt={link.name}
                    className="w-12 h-12 mr-4"
                  />
                )}
                <span className="text-gray-700 font-semibold">{link.name}</span>
              </a>
            ))}

            {/* Social Media Icons */}
            <div className="flex justify-center items-center gap-4 mt-6">
              <a
                href={SOCIAL_MEDIA_LINKS.FACEBOOK}
                target="_blank"
                rel="noopener noreferrer"
                className="transform transition hover:scale-110"
              >
                <img
                  src="https://bloblandingpageprd.blob.core.windows.net/toyota/geral/linktree/facebook.svg"
                  alt="Facebook"
                  className="w-15 h-13"
                />
              </a>
              {/*<a
                href="https://www.tiktok.com/@kurumatoyota"
                rel="noopener noreferrer"
                className="transform transition hover:scale-110"
              >
                <img
                  src="https://bloblandingpageprd.blob.core.windows.net/toyota/geral/linktree/tiktok.svg"
                  alt="TikTok"
                  className="w-15 h-13"
                />
              </a>*/}
              <a
                href={SOCIAL_MEDIA_LINKS.YOUTUBE}
                target="_blank"
                rel="noopener noreferrer"
                className="transform transition hover:scale-110"
              >
                <img
                  src="https://bloblandingpageprd.blob.core.windows.net/toyota/geral/linktree/youtube.svg"
                  alt="YouTube"
                  className="w-15 h-13"
                />
              </a>
              <a
                href={SOCIAL_MEDIA_LINKS.LINKEDIN}
                target="_blank"
                rel="noopener noreferrer"
                className="transform transition hover:scale-110"
              >
                <img
                  src="https://bloblandingpageprd.blob.core.windows.net/toyota/geral/linktree/linkedin.svg"
                  alt="Linkedin"
                  className="w-15 h-13"
                />
              </a>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
